/* =========================================================
   Safe fallbacks: only used if the theme does not already
   define these helpers in another asset file
   ========================================================= */
if (typeof window.debounce !== "function") {
  window.debounce = function (fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
if (typeof window.trapFocus !== "function") {
  window.trapFocus = function (container, elementToFocus) {
    const el = elementToFocus || container;
    if (el && typeof el.focus === "function") el.focus();
  };
}
if (typeof window.removeTrapFocus !== "function") {
  window.removeTrapFocus = function (elementToFocus) {
    if (elementToFocus && typeof elementToFocus.focus === "function") elementToFocus.focus();
  };
}
if (typeof window.pauseAllMedia !== "function") {
  window.pauseAllMedia = function () {
    document.querySelectorAll("video").forEach((v) => { try { v.pause(); } catch (e) {} });
  };
}

function defineElement(name, ctor) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}

/* ========== Focus visible polyfill ========== */
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (event.code && navKeys.includes(event.code.toUpperCase())) mouseClick = false;
  });
  window.addEventListener("mousedown", () => { mouseClick = true; });
  window.addEventListener("focus", () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove("focused");
    if (mouseClick) return;
    currentFocusedElement = document.activeElement;
    if (currentFocusedElement && currentFocusedElement.classList) currentFocusedElement.classList.add("focused");
  }, true);
}

/* ========== Product recommendations ========== */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.observer) return;

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
            this.remove();
            return;
          }

          if (html.querySelector(".grid__item")) this.classList.add("product-recommendations--loaded");
        })
        .catch((e) => console.error(e));
    };

    this.observer = new IntersectionObserver(handleIntersection, { rootMargin: "0px 0px 400px 0px" });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
defineElement("product-recommendations", ProductRecommendations);

/* ========== Menu drawer ========== */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    if (!this.mainDetailsToggle) return;
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.menu-drawer__close-menu-btn)").forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (!event.code || event.code.toUpperCase() !== "ESCAPE") return;
    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      const panel = summaryElement.nextElementSibling;
      if (!panel) return;
      trapFocus(panel, detailsElement.querySelector("button"));
      panel.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)").matches) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        if (parentMenuElement) parentMenuElement.classList.add("submenu-open");

        const panel = summaryElement.nextElementSibling;
        if (!reducedMotion || reducedMotion.matches || !panel) {
          addTrapFocus();
        } else {
          panel.addEventListener("transitionend", addTrapFocus);
        }
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => this.mainDetailsToggle.classList.add("menu-opening"));
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => submenu.classList.remove("submenu-open"));
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (detailsElement) this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    if (!detailsElement) return;
    const parentMenuElement = detailsElement.closest(".submenu-open");
    if (parentMenuElement) parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");

    const summary = detailsElement.querySelector("summary");
    if (summary) {
      summary.setAttribute("aria-expanded", false);
      removeTrapFocus(summary);
    }
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;
    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;
      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        const parentDetails = detailsElement.closest("details[open]");
        if (parentDetails) trapFocus(parentDetails, detailsElement.querySelector("summary"));
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }
}
defineElement("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.querySelectorAll(".menu-drawer__close-menu-btn").forEach((btn) => btn.addEventListener("click", this.closeButtonClick.bind(this)));
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    const wrapper = this.closest(".header-wrapper");
    this.borderOffset = this.borderOffset || (wrapper && wrapper.classList.contains("header-wrapper--border-bottom") ? 1 : 0);

    if (this.header) {
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
      this.header.classList.add("menu-open");
    }

    setTimeout(() => this.mainDetailsToggle.classList.add("menu-opening"));
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (event === undefined) return;
    super.closeMenuDrawer(event, elementToFocus);
    if (this.header) this.header.classList.remove("menu-open");
  }

  closeButtonClick(event) {
    this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"));
    const toggle = this.querySelector(".header__icon--menu[aria-expanded=true]");
    if (toggle) toggle.setAttribute("aria-expanded", "false");
  }
}
defineElement("header-drawer", HeaderDrawer);

/* ========== Products mega menu ========== */
class ProductsMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details[id^="ProductsMegaMenu-"]');
    this.mainLink = this.querySelector("summary.header__menu-item");
    this.body = this.querySelector(".products-mega-menu__body");
    this.overlay = this.querySelector(".products-mega-menu__overlay");
    this.items = this.querySelectorAll('[id^="ProductsMegaMenu-Item"]');
    this.links = this.querySelectorAll('[id^="ProductsMegaMenu-Link"]');
    if (!this.details || !this.mainLink) return;

    this.onOutsideClick = this.outsideClick.bind(this);
    this.mainLink.addEventListener("mouseover", this.openMenu.bind(this));
    if (this.overlay) {
      this.overlay.addEventListener("mouseover", this.closeMenu.bind(this));
      this.overlay.addEventListener("click", this.closeMenu.bind(this));
    }
    this.links.forEach((link) => link.addEventListener("mouseover", this.displayContent.bind(this)));
  }

  connectedCallback() {
    if (this.onOutsideClick) document.addEventListener("click", this.onOutsideClick);
  }

  disconnectedCallback() {
    if (this.onOutsideClick) document.removeEventListener("click", this.onOutsideClick);
  }

  displayContent(event) {
    const item = event.target.closest('[id^="ProductsMegaMenu-Item"]');
    if (!item) return;
    this.items.forEach((el) => el.classList.remove("products-mega-menu__item--active"));
    item.classList.add("products-mega-menu__item--active");
  }

  openMenu() {
    document.querySelectorAll('[id^="Details-HeaderMenu"], [id^="ProductsMegaMenu-"]').forEach((el) => el.removeAttribute("open"));
    this.details.setAttribute("open", "");
  }

  closeMenu() {
    this.details.removeAttribute("open");
  }

  outsideClick(event) {
    if (!this.details.hasAttribute("open")) return;
    if (this.body && (event.target === this.body || this.body.contains(event.target))) return;
    this.closeMenu();
  }
}
defineElement("products-mega-menu", ProductsMegaMenu);

/* ========== Modal dialog ========== */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) closeButton.addEventListener("click", this.hide.bind(this, false));

    this.addEventListener("keyup", (event) => {
      if (event.code && event.code.toUpperCase() === "ESCAPE") this.hide();
    });

    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup && typeof popup.loadContent === "function") popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
defineElement("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector("button:not(.internal-video__play, .internal-video__sound-btn)");
    if (!button) return;

    button.addEventListener("click", () => {
      const selector = this.getAttribute("data-modal");
      const modal = selector ? document.querySelector(selector) : null;
      if (modal && typeof modal.show === "function") modal.show(button);
    });
  }
}
defineElement("modal-opener", ModalOpener);

/* ========== Deferred media ========== */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", () => this.loadContent());
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (this.getAttribute("loaded")) return;

    const template = this.querySelector("template");
    if (!template || !template.content.firstElementChild) return;

    const content = document.createElement("div");
    content.appendChild(template.content.firstElementChild.cloneNode(true));
    const media = content.querySelector("video, model-viewer, iframe");
    if (!media) return;

    this.setAttribute("loaded", true);
    const deferredElement = this.appendChild(media);
    if (focus && typeof deferredElement.focus === "function") deferredElement.focus();
  }
}
defineElement("deferred-media", DeferredMedia);

/* ========== Copy button ========== */
class CopyButton extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener("click", this.handleClick.bind(this));
  }

  handleClick() {
    const text = (this.dataset.content || "").trim();
    const done = () => {
      this.dataset.success = "true";
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => (this.dataset.success = "false"), 3000);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done).catch(() => this.legacyCopy(text, done));
    } else {
      this.legacyCopy(text, done);
    }
  }

  legacyCopy(text, done) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.className = "visually-hidden";
    this.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      done();
    } catch (e) {
      console.error(e);
    }
    textarea.remove();
  }
}
defineElement("copy-button", CopyButton);

/* ========== Slider ========== */
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.sliderItemsToShow = [];
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    this.resizeObserver = new ResizeObserver(() => this.initPages());
    this.resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    if (this.prevButton) this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));

    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    if (!this.sliderControlWrapper || !this.sliderControlWrapper.querySelector(".slider-counter__link")) return;

    this.internalVideos = this.querySelectorAll("internal-video");
    this.pauseVideos = this.dataset.pauseVideos === "true" && this.internalVideos.length > 0;
    this.updateActive = this.dataset.updateActive === "true";
    this.sliderFirstItemNode = this.slider.querySelector(".slider__slide");
    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.linksBound = true;
  }

  initPages() {
    if (!this.slider) return;
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((el) => el.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    if (!this.sliderItemOffset) return;

    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent("slideChanged", {
        detail: { currentPage: this.currentPage, currentElement: this.sliderItemsToShow[this.currentPage - 1] }
      }));
    }

    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    if (this.sliderControlButtons.length > 0) {
      this.sliderControlButtons.forEach((btn) => {
        btn.classList.remove("slider-counter__link--active");
        btn.removeAttribute("aria-current");
      });
      const activeButton = this.sliderControlButtons[this.currentPage - 1];
      if (activeButton) {
        activeButton.classList.add("slider-counter__link--active");
        activeButton.setAttribute("aria-current", true);
      }
    }

    const activeSlide = this.sliderItems[this.currentPage - 1];
    if (this.pauseVideos && activeSlide) {
      this.internalVideos.forEach((video) => {
        const slide = video.closest('[id^="Slide-"]');
        if (!slide || activeSlide.id == slide.id) return;
        const videoEl = video.querySelector("video");
        if (!videoEl) return;

        if (video.dataset.autoplay === "true") {
          videoEl.muted = true;
          video.classList.add("internal-video--muted");
        } else {
          videoEl.pause();
          video.classList.remove("internal-video--playing");
        }
      });
    }

    if (this.enableSliderLooping) return;

    if (this.prevButton) {
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], true)) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = false) {
    if (!element) return false;
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft + (offset ? 10 : 0);
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide;
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.sliderItemOffset) return;
    const step = parseInt(event.currentTarget.dataset.step, 10) || 1;
    this.slideScrollPosition = event.currentTarget.name === "next"
      ? this.slider.scrollLeft + step * this.sliderItemOffset
      : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({ left: this.slideScrollPosition });
  }

  linkToSlide(event) {
    event.preventDefault();
    if (!this.sliderFirstItemNode || !this.sliderControlLinksArray) return;
    const slideScrollPosition = this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({ left: slideScrollPosition });
  }
}
defineElement("slider-component", SliderComponent);

/* ========== Countdown timer ========== */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.init();
    if (this.dataset.autoPlay === "true") this.playTimer();
  }

  disconnectedCallback() {
    this.pauseTimer();
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.innerHTML = "";
    this.minutesSpan = document.createElement("span");
    this.secondsSpan = document.createElement("span");
    this.append(this.minutesSpan, document.createTextNode(":"), this.secondsSpan);
    this.updateTimer();
  }

  updateTimer() {
    if (!this.initialized) return this.init();
    let duration = parseInt(this.dataset.duration, 10);
    if (isNaN(duration) || duration <= 0) duration = 90;

    this.minutesSpan.textContent = this.formatNumber(Math.floor(duration / 60));
    this.secondsSpan.textContent = this.formatNumber(duration % 60);
    this.dataset.duration = duration - 1;
  }

  playTimer() {
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.playInterval = setInterval(() => this.updateTimer(), 1000);
  }

  pauseTimer() {
    clearInterval(this.playInterval);
    this.isPlaying = false;
  }

  formatNumber(number) {
    return number < 10 ? "0" + number : String(number);
  }
}
defineElement("countdown-timer", CountdownTimer);

/* ========== Slideshow ========== */
class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.slider || !this.nextButton || !this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    if (!this.linksBound) {
      this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
      this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
      this.linksBound = true;
    }

    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  disconnectedCallback() {
    clearInterval(this.autoplay);
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    const speed = parseFloat(this.slider.dataset.speed);
    this.autoplaySpeed = (speed > 0 ? speed : 5) * 1000;

    if (this.sliderAutoplayButton) this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({ left: this.slideScrollPosition });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    if (this.prevButton) this.prevButton.removeAttribute("disabled");
    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    const active = this.sliderControlButtons[this.currentPage - 1];
    if (active) {
      active.classList.add("slider-counter__link--active");
      active.setAttribute("aria-current", true);
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  isAutoplayButtonEvent(event) {
    const btn = this.sliderAutoplayButton;
    return !!btn && (event.target === btn || btn.contains(event.target));
  }

  focusOutHandling(event) {
    if (this.autoplayButtonIsSetToPlay && !this.isAutoplayButtonEvent(event)) this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = this.isAutoplayButtonEvent(event);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (!this.sliderAutoplayButton) return;
    const strings = window.accessibilityStrings || {};
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", strings.playSlideshow || "Play slideshow");
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", strings.pauseSlideshow || "Pause slideshow");
    }
  }

  autoRotateSlides() {
    const firstSlide = this.slider.querySelector(".slideshow__slide");
    const slideScrollPosition = this.currentPage === this.sliderItems.length || !firstSlide
      ? 0
      : this.slider.scrollLeft + firstSlide.clientWidth;
    this.slider.scrollTo({ left: slideScrollPosition });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        linkElements.forEach((button) => button.removeAttribute("tabindex"));
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        linkElements.forEach((button) => button.setAttribute("tabindex", "-1"));
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }
}
defineElement("slideshow-component", SlideshowComponent);

/* ========== Splide (library, kept intact) ========== */
function _defineProperties(t,e){for(var i=0;i<e.length;i++){var s=e[i];s.enumerable=s.enumerable||!1,s.configurable=!0,"value"in s&&(s.writable=!0),Object.defineProperty(t,s.key,s)}}function _createClass(t,e,i){return e&&_defineProperties(t.prototype,e),i&&_defineProperties(t,i),Object.defineProperty(t,"prototype",{writable:!1}),t}
(function(t,e){var s=e();"object"==typeof exports&&"undefined"!=typeof module&&(module.exports=s);t="undefined"!=typeof globalThis?globalThis:t||self;t.ThemeSplide=s;t.Splide=s})(this,function(){"use strict";var t="(prefers-reduced-motion: reduce)";function e(t){t.length=0}function i(t,e,i){return Array.prototype.slice.call(t,e,i)}function s(t){return t.bind.apply(t,[null].concat(i(arguments,1)))}var n=setTimeout,o=function t(){};function r(t){return requestAnimationFrame(t)}function a(t,e){return typeof e===t}function l(t){return!p(t)&&a("object",t)}var d=Array.isArray,c=s(a,"function"),u=s(a,"string"),h=s(a,"undefined");function p(t){return null===t}function f(t){try{return t instanceof(t.ownerDocument.defaultView||window).HTMLElement}catch(e){return!1}}function g(t){return d(t)?t:[t]}function v(t,e){g(t).forEach(e)}function m(t,e){return t.indexOf(e)>-1}function y(t,e){return t.push.apply(t,g(e)),t}function b(t,e,i){t&&v(e,function(e){e&&t.classList[i?"add":"remove"](e)})}function $(t,e){b(t,u(e)?e.split(" "):e,!0)}function S(t,e){v(e,t.appendChild.bind(t))}function E(t,e){v(t,function(t){var i=(e||t).parentNode;i&&i.insertBefore(t,e)})}function C(t,e){return f(t)&&(t.msMatchesSelector||t.matches).call(t,e)}function L(t,e){var s=t?i(t.children):[];return e?s.filter(function(t){return C(t,e)}):s}function k(t,e){return e?L(t,e)[0]:t.firstElementChild}var x=Object.keys;function _(t,e,i){return t&&(i?x(t).reverse():x(t)).forEach(function(i){"__proto__"!==i&&e(t[i],i)}),t}function A(t){return i(arguments,1).forEach(function(e){_(e,function(i,s){t[s]=e[s]})}),t}function w(t){return i(arguments,1).forEach(function(e){_(e,function(e,i){d(e)?t[i]=e.slice():l(e)?t[i]=w({},l(t[i])?t[i]:{},e):t[i]=e})}),t}function B(t,e){v(e||x(t),function(e){delete t[e]})}function M(t,e){v(t,function(t){v(e,function(e){t&&t.removeAttribute(e)})})}function P(t,e,i){l(e)?_(e,function(e,i){P(t,i,e)}):v(t,function(t){p(i)||""===i?M(t,e):t.setAttribute(e,String(i))})}function T(t,e,i){var s=document.createElement(t);return e&&(u(e)?$(s,e):P(s,e)),i&&S(i,s),s}function q(t,e,i){if(h(i))return getComputedStyle(t)[e];p(i)||(t.style[e]=""+i)}function D(t,e){q(t,"display",e)}function I(t){t.setActive&&t.setActive()||t.focus({preventScroll:!0})}function O(t,e){return t.getAttribute(e)}function H(t,e){return t&&t.classList.contains(e)}function N(t){return t.getBoundingClientRect()}function W(t){v(t,function(t){t&&t.parentNode&&t.parentNode.removeChild(t)})}function R(t){return k(new DOMParser().parseFromString(t,"text/html").body)}function z(t,e){t.preventDefault(),e&&(t.stopPropagation(),t.stopImmediatePropagation())}function F(t,e){return t&&t.querySelector(e)}function X(t,e){return e?i(t.querySelectorAll(e)):[]}function V(t,e){b(t,e,!1)}function G(t){return t.timeStamp}function U(t){return u(t)?t:t?t+"px":""}var j="splide",Y="data-"+j;function K(t,e){if(!t)throw Error("["+j+"] "+(e||""))}var J=Math.min,Q=Math.max,Z=Math.floor,tt=Math.ceil,te=Math.abs;function ti(t,e,i){return te(t-e)<i}function ts(t,e,i,s){var n=J(e,i),o=Q(e,i);return s?n<t&&t<o:n<=t&&t<=o}function tn(t,e,i){var s=J(e,i),n=Q(e,i);return J(Q(s,t),n)}function to(t){return+(t>0)-+(t<0)}function tr(t,e){return v(e,function(e){t=t.replace("%s",""+e)}),t}function ta(t){return t<10?"0"+t:""+t}var tl={};function td(){var t=[];function i(t,e,i){v(t,function(t){t&&v(e,function(e){e.split(" ").forEach(function(e){var s=e.split(".");i(t,s[0],s[1])})})})}return{bind:function e(s,n,o,r){i(s,n,function(e,i,s){var n="addEventListener"in e,a=n?e.removeEventListener.bind(e,i,o,r):e.removeListener.bind(e,o);n?e.addEventListener(i,o,r):e.addListener(o),t.push([e,i,s,o,a])})},unbind:function e(s,n,o){i(s,n,function(e,i,s){t=t.filter(function(t){return t[0]!==e||t[1]!==i||t[2]!==s||!!o&&t[3]!==o||(t[4](),!1)})})},dispatch:function t(e,i,s){var n;return"function"==typeof CustomEvent?n=new CustomEvent(i,{bubbles:!0,detail:s}):(n=document.createEvent("CustomEvent")).initCustomEvent(i,!0,!1,s),e.dispatchEvent(n),n},destroy:function i(){t.forEach(function(t){t[4]()}),e(t)}}}var tc="mounted",tu="ready",th="move",tp="moved",tf="click",tg="refresh",tv="updated",tm="resize",ty="resized",tb="scroll",t$="scrolled",tS="destroy",tE="navigation:mounted",tC="autoplay:play",tL="autoplay:pause",tk="lazyload:loaded";function tx(t){var e=t?t.event.bus:document.createDocumentFragment(),n=td();return t&&t.event.on(tS,n.destroy),A(n,{bus:e,on:function t(i,s){n.bind(e,g(i).join(" "),function(t){s.apply(s,d(t.detail)?t.detail:[])})},off:s(n.unbind,e),emit:function t(s){n.dispatch(e,s,i(arguments,1))}})}function t_(t,e,i,s){var n,o,a=Date.now,l=0,d=!0,c=0;function u(){if(!d){if(l=t?J((a()-n)/t,1):1,i&&i(l),l>=1&&(e(),n=a(),s&&++c>=s))return h();o=r(u)}}function h(){d=!0}function p(){o&&cancelAnimationFrame(o),l=0,o=0,d=!0}function f(e){t=e}function g(){return d}return{start:function e(i){i||p(),n=a()-(i?l*t:0),d=!1,o=r(u)},rewind:function t(){n=a(),l=0,i&&i(l)},pause:h,cancel:p,set:f,isPaused:g}}var tA="Arrow",tw=tA+"Left",tB=tA+"Right",tM=tA+"Up",t8=tA+"Down",tP={width:["height"],left:["top","right"],right:["bottom","left"],x:["y"],X:["Y"],Y:["X"],ArrowLeft:[tM,tB],ArrowRight:[t8,tw]},tT="role",tq="tabindex",tD="aria-",tI=tD+"controls",tO=tD+"current",tH=tD+"selected",tN=tD+"label",tW=tD+"labelledby",tR=tD+"hidden",t2=tD+"orientation",tz=tD+"roledescription",tF=tD+"live",t3=tD+"busy",t9=tD+"atomic",tX=[tT,tq,"disabled",tI,tO,tN,tW,tR,t2,tz],t0=j+"__",t1=j,tV=t0+"track",t4=t0+"list",tG=t0+"slide",tU=tG+"--clone",tj=tG+"__container",t6=t0+"arrows",tY=t0+"arrow",t7=tY+"--prev",tK=tY+"--next",t5=t0+"pagination",tJ=t5+"__page",tQ=t0+"progress__bar",tZ=t0+"toggle",et=t0+"sr",ee="is-active",ei="is-prev",es="is-next",en="is-visible",eo="is-loading",er="is-focus-in",ea="is-overflow",el=[ee,en,ei,es,eo,er,ea],ed="touchstart mousedown",ec="touchmove mousemove",eu="touchend touchcancel mouseup click",eh="slide",ep="loop",ef="fade",eg=Y+"-interval",ev={passive:!1,capture:!0},em={Spacebar:" ",Right:tB,Left:tw,Up:tM,Down:t8};function ey(t){return em[t=u(t)?t:t.key]||t}var eb="keydown",e$=Y+"-lazy",eS=e$+"-srcset",eE="["+e$+"], ["+eS+"]",eC=[" ","Enter"],eL=Object.freeze({__proto__:null,Media:function e(i,s,n){var o=i.state,r=n.breakpoints||{},a=n.reducedMotion||{},l=td(),d=[];function c(t){t&&l.destroy()}function u(t,e){var i=matchMedia(e);l.bind(i,"change",h),d.push([t,i])}function h(){var t=o.is(7),e=n.direction,s=d.reduce(function(t,e){return w(t,e[1].matches?e[0]:{})},{});B(n),p(s),n.destroy?i.destroy("completely"===n.destroy):t?(c(!0),i.mount()):e!==n.direction&&i.refresh()}function p(t,e,s){w(n,t),e&&w(Object.getPrototypeOf(n),t),(s||!o.is(1))&&i.emit(tv,n)}return{setup:function e(){var i="min"===n.mediaQuery;x(r).sort(function(t,e){return i?+t-+e:+e-+t}).forEach(function(t){u(r[t],"("+(i?"min":"max")+"-width:"+t+"px)")}),u(a,t),h()},destroy:c,reduce:function e(i){matchMedia(t).matches&&(i?w(n,a):B(n,x(a)))},set:p}},Direction:function t(e,i,s){return{resolve:function t(e,i,n){var o="rtl"!==(n=n||s.direction)||i?"ttb"===n?0:-1:1;return tP[e]&&tP[e][o]||e.replace(/width|left|right/i,function(t,e){var i=tP[t.toLowerCase()][o]||t;return e>0?i.charAt(0).toUpperCase()+i.slice(1):i})},orient:function t(e){return e*("rtl"===s.direction?1:-1)}}},Elements:function t(i,s,n){var o,r,a,l=tx(i),d=l.on,u=l.bind,h=i.root,p=n.i18n,f={},g=[],v=[],m=[];function S(){var t,e,i;o=w("."+tV),r=k(o,"."+t4),K(o&&r,"A track/list element is missing."),y(g,L(r,"."+tG+":not(."+tU+")")),_({arrows:t6,pagination:t5,prev:t7,next:tK,bar:tQ,toggle:tZ},function(t,e){f[e]=w("."+t)}),A(f,{root:h,track:o,list:r,slides:g}),e=h.id||""+(t=j)+ta(tl[t]=(tl[t]||0)+1),i=n.role,h.id=e,o.id=o.id||e+"-track",r.id=r.id||e+"-list",!O(h,tT)&&"SECTION"!==h.tagName&&i&&P(h,tT,i),P(h,tz,p.carousel),P(r,tT,"presentation"),x()}function E(t){var i=tX.concat("style");e(g),V(h,v),V(o,m),M([o,r],i),M(h,t?i:["style",tz])}function x(){V(h,v),V(o,m),v=B(t1),m=B(tV),$(h,v),$(o,m),P(h,tN,n.label),P(h,tW,n.labelledby)}function w(t){var e=F(h,t);return e&&function t(e,i){if(c(e.closest))return e.closest(i);for(var s=e;s&&1===s.nodeType&&!C(s,i);)s=s.parentElement;return s}(e,"."+t1)===h?e:void 0}function B(t){return[t+"--"+n.type,t+"--"+n.direction,n.drag&&t+"--draggable",n.isNavigation&&t+"--nav",t===t1&&ee]}return A(f,{setup:S,mount:function t(){d(tg,E),d(tg,S),d(tv,x),u(document,ed+" keydown",function(t){a="keydown"===t.type},{capture:!0}),u(h,"focusin",function(){b(h,er,!!a)})},destroy:E})},Slides:function t(i,n,o){var r=tx(i),a=r.on,l=r.emit,d=r.bind,h=n.Elements,p=h.slides,y=h.list,L=[];function x(){p.forEach(function(t,e){A(t,e,-1)})}function _(){B(function(t){t.destroy()}),e(L)}function A(t,e,n){var o=function t(e,i,n,o){var r,a=tx(e),l=a.on,d=a.emit,c=a.bind,u=e.Components,h=e.root,p=e.options,f=p.isNavigation,g=p.updateOnMove,v=p.i18n,m=p.pagination,y=p.slideFocus,$=u.Direction.resolve,S=O(o,"style"),E=O(o,tN),C=n>-1,L=k(o,"."+tj);function x(){var t=e.splides.map(function(t){var e=t.splide.Components.Slides.getAt(i);return e?e.slide.id:""}).join(" ");P(o,tN,tr(v.slideX,(C?n:i)+1)),P(o,tI,t),P(o,tT,y?"button":""),y&&M(o,tz)}function _(){r||A()}function A(){if(!r){var t,s=e.index;(t=w())!==H(o,ee)&&(b(o,ee,t),P(o,tO,f&&t||""),d(t?"active":"inactive",B)),function t(){var i=function t(){if(e.is(ef))return w();var i=N(u.Elements.track),s=N(o),n=$("left",!0),r=$("right",!0);return Z(i[n])<=tt(s[n])&&Z(s[r])<=tt(i[r])}(),s=!i&&(!w()||C);if(e.state.is([4,5])||P(o,tR,s||""),P(X(o,p.focusableNodes||""),tq,s?-1:""),y&&P(o,tq,s?-1:0),i!==H(o,en)&&(b(o,en,i),d(i?"visible":"hidden",B)),!i&&document.activeElement===o){var n=u.Slides.getAt(e.index);n&&I(n.slide)}}(),b(o,ei,i===s-1),b(o,es,i===s+1)}}function w(){var t=e.index;return t===i||p.cloneStatus&&t===n}var B={index:i,slideIndex:n,slide:o,container:L,isClone:C,mount:function t(){C||(o.id=h.id+"-slide"+ta(i+1),P(o,tT,m?"tabpanel":"group"),P(o,tz,v.slide),P(o,tN,E||tr(v.slideLabel,[i+1,e.length]))),c(o,"click",s(d,tf,B)),c(o,"keydown",s(d,"sk",B)),l([tp,"sh",t$],A),l(tE,x),g&&l(th,_)},destroy:function t(){r=!0,a.destroy(),V(o,el),M(o,tX),P(o,"style",S),P(o,tN,E||"")},update:A,style:function t(e,i,s){q(s&&L||o,e,i)},isWithin:function t(s,n){var o=te(s-i);return!C&&(p.rewind||e.is(ep))&&(o=J(o,e.length-o)),o<=n}};return B}(i,e,n,t);o.mount(),L.push(o),L.sort(function(t,e){return t.index-e.index})}function w(t){return t?T(function(t){return!t.isClone}):L}function B(t,e){w(e).forEach(t)}function T(t){return L.filter(c(t)?t:function(e){return u(t)?C(e.slide,t):m(g(t),e.index)})}return{mount:function t(){x(),a(tg,_),a(tg,x)},destroy:_,update:function t(){B(function(t){t.update()})},register:A,get:w,getIn:function t(e){var i=n.Controller,s=i.toIndex(e),r=i.hasFocus()?1:o.perPage;return T(function(t){return ts(t.index,s,s+r-1)})},getAt:function t(e){return T(e)[0]},add:function t(e,i){v(e,function(t){if(u(t)&&(t=R(t)),f(t)){var e,n,r,a,c=p[i];c?E(t,c):S(y,t),$(t,o.classes.slide),e=t,n=s(l,tm),(a=(r=X(e,"img")).length)?r.forEach(function(t){d(t,"load error",function(){--a||n()})}):n()}}),l(tg)},remove:function t(e){W(T(e).map(function(t){return t.slide})),l(tg)},forEach:B,filter:T,style:function t(e,i,s){B(function(t){t.style(e,i,s)})},getLength:function t(e){return e?p.length:L.length},isEnough:function t(){return L.length>o.perPage}}},Layout:function t(e,i,n){var o,r,a,d=tx(e),c=d.on,u=d.bind,h=d.emit,p=i.Slides,f=i.Direction.resolve,g=i.Elements,v=g.root,m=g.track,y=g.list,$=p.getAt,S=p.style;function E(){o="ttb"===n.direction,q(v,"maxWidth",U(n.width)),q(m,f("paddingLeft"),L(!1)),q(m,f("paddingRight"),L(!0)),C(!0)}function C(t){var e,i=N(v);(t||r.width!==i.width||r.height!==i.height)&&(q(m,"height",(e="",o&&(e=k(),K(e,"height or heightRatio is missing."),e="calc("+e+" - "+L(!1)+" - "+L(!0)+")"),e)),S(f("marginRight"),U(n.gap)),S("width",n.autoWidth?null:U(n.fixedWidth)||(o?"":x())),S("height",U(n.fixedHeight)||(o?n.autoHeight?null:x():k()),!0),r=i,h(ty),a!==(a=P())&&(b(v,ea,a),h("overflow",a)))}function L(t){var e=n.padding,i=f(t?"right":"left");return e&&U(e[i]||(l(e)?0:e))||"0px"}function k(){return U(n.height||N(y).width*n.heightRatio)}function x(){var t=U(n.gap);return"calc((100%"+(t&&" + "+t)+")/"+(n.perPage||1)+(t&&" - "+t)+")"}function _(){return N(y)[f("width")]}function A(t,e){var i=$(t||0);return i?N(i.slide)[f("width")]+(e?0:M()):0}function w(t,e){var i=$(t);if(i){var s=N(i.slide)[f("right")],n=N(y)[f("left")];return te(s-n)+(e?0:M())}return 0}function B(t){return w(e.length-1)-w(0)+A(0,t)}function M(){var t=$(0);return t&&parseFloat(q(t.slide,f("marginRight")))||0}function P(){return e.is(ef)||B(!0)>_()}return{mount:function t(){var e,i;E(),u(window,"resize load",(e=s(h,tm),i=t_(0,e,null,1),function(){i.isPaused()&&i.start()})),c([tv,tg],E),c(tm,C)},resize:C,listSize:_,slideSize:A,sliderSize:B,totalSize:w,getPadding:function t(e){return parseFloat(q(m,f("padding"+(e?"Right":"Left"))))||0},isOverflow:P}},Clones:function t(i,s,n){var o,r=tx(i),a=r.on,l=s.Elements,d=s.Slides,c=s.Direction.resolve,u=[];function p(){a(tg,f),a([tv,tm],v),(o=m())&&(function t(e){var s=d.get().slice(),o=s.length;if(o){for(;s.length<e;)y(s,s);y(s.slice(-e),s.slice(0,e)).forEach(function(t,r){var a,c,h,p=r<e,f=(a=t.slide,c=r,h=a.cloneNode(!0),$(h,n.classes.clone),h.id=i.root.id+"-clone"+ta(c+1),h);p?E(f,s[0].slide):S(l.list,f),y(u,f),d.register(f,r-e+(p?0:o),t.index)})}}(o),s.Layout.resize(!0))}function f(){g(),p()}function g(){W(u),e(u),r.destroy()}function v(){var t=m();o!==t&&(o<t||!t)&&r.emit(tg)}function m(){var t=n.clones;if(i.is(ep)){if(h(t)){var e=n[c("fixedWidth")]&&s.Layout.slideSize(0);t=e&&tt(N(l.track)[c("width")]/e)||n[c("autoWidth")]&&i.length||2*n.perPage}}else t=0;return t}return{mount:p,destroy:g}},Move:function t(e,i,s){var n,o=tx(e),r=o.on,a=o.emit,l=e.state.set,d=e.state,c=i.Layout,u=c.slideSize,p=c.getPadding,f=c.totalSize,g=c.listSize,v=c.sliderSize,m=i.Direction,y=m.resolve,b=m.orient,$=i.Elements,S=$.list,E=$.track,C=0;function L(){return C}function k(){i.Controller.isBusy()||(i.Scroll.cancel(),x(e.index),i.Slides.update())}function x(t){_(M(t,!0))}function _(t,n){if(!e.is(ef)){s.paddingCalc&&"slide"===s.type&&!d.is(6)&&(C=t>=-1*s.padding.right?0:v()+t-s.padding.right<=u()?s.padding.right:s.padding.right/2);var o=n?t:function t(s){if(e.is(ep)){var n=B(s),o=n>i.Controller.getEnd();(n<0||o)&&(s=A(s,o))}return s}(t);q(S,"transform",`translate${y("X")}(${o+C}px)`),t!==o&&a("sh")}}function A(t,e){var i=t-T(e),s=v();return t-b(s*(tt(te(i)/s)||1))*(e?1:-1)}function w(){_(P(),!0),n.cancel()}function B(t){for(var e=i.Slides.get(),s=0,n=1/0,o=0;o<e.length;o++){var r=e[o].index,a=te(M(r,!0)-t);if(a<=n)n=a,s=r;else break}return s}function M(t,i){var n,o,r,a=b(f(t-1)-(n=t,o=s.focus,"center"===o?(g()-u(n,!0))/2:+o*u(n)||0));return i?(r=a,s.trimSpace&&e.is(eh)&&(r=tn(r,0,b(v(!0)-g()))),r):a}function P(){var t=y("left");return N(S)[t]-N(E)[t]+b(p(!1))-C}function T(t){return M(t?i.Controller.getEnd():0,!!s.trimSpace)}return{mount:function t(){n=i.Transition,r([tc,ty,tv,tg],k)},move:function t(e,i,s,o){var r,d;e!==i&&(r=e>s,d=b(A(P(),r)),r?d>=0:d<=S[y("scrollWidth")]-N(E)[y("width")])&&(w(),_(A(P(),e>s),!0)),l(4),a(th,i,s,e),n.start(i,function(){l(3),a(tp,i,s,e),o&&o()})},jump:x,translate:_,shift:A,cancel:w,toIndex:B,toPosition:M,getPosition:P,getLimit:T,exceededLimit:function t(e,i){i=h(i)?P():i;var s=!0!==e&&b(i)<b(T(!1)),n=!1!==e&&b(i)>b(T(!0));return s||n},reposition:k}},Controller:function t(e,i,n){var o,r,a,l,d=tx(e),c=d.on,p=d.emit,f=i.Move,g=f.getPosition,v=f.getLimit,m=f.toPosition,y=i.Slides,b=y.isEnough,$=y.getLength,S=n.omitEnd,E=e.is(ep),C=e.is(eh),L=s(B,!1),k=s(B,!0),x=n.start||0,_=x;function A(){r=$(!0),a=n.perMove,l=n.perPage,o=T();var t=tn(x,0,S?o:r-1);t!==x&&(x=t,f.reposition())}function w(){o!==T()&&p("ei")}function B(t,e){var i,s,n=a||(O()?1:l),r=M(x+n*(t?-1:1),x,!(a||O()));if(-1===r&&C){if(i=g(),!(1>te(i-(s=v(!t)))))return t?0:o}return e?r:P(r)}function M(t,i,s){if(b()||O()){var d=function t(i){if(C&&"move"===n.trimSpace&&i!==x)for(var s=g();s===m(i,!0)&&ts(i,0,e.length-1,!n.rewind);)i<x?--i:++i;return i}(t);d!==t&&(i=t,t=d,s=!1),t<0||t>o?t=!a&&(ts(0,t,i,!0)||ts(o,i,t,!0))?q(D(t)):E?s?t<0?-(r%l||l):r:t:n.rewind?t<0?o:0:-1:s&&t!==i&&(t=q(D(i)+(t<i?-1:1)))}else t=-1;return t}function P(t){return E?(t+r)%r||0:t}function T(){for(var t=r-(O()||E&&a?1:l);S&&t-- >0;)if(m(r-1,!0)!==m(t,!0)){t++;break}return tn(t,0,r-1)}function q(t){return tn(O()?t:l*t,0,o)}function D(t){return O()?J(t,o):Z((t>=o?r-1:t)/l)}function I(t){t!==x&&(_=x,x=t)}function O(){return!h(n.focus)||n.isNavigation}function H(){return e.state.is([4,5])&&!!n.waitForTransition}return{mount:function t(){A(),c([tv,tg,"ei"],A),c(ty,w)},go:function t(e,i,s){if(!H()){var n=function t(e){var i=x;if(u(e)){var s=e.match(/([+\-<>])(\d+)?/)||[],n=s[1],r=s[2];"+"===n||"-"===n?i=M(x+ +(""+n+(+r||1)),x):">"===n?i=r?q(+r):L(!0):"<"===n&&(i=k(!0))}else i=E?e:tn(e,0,o);return i}(e),r=P(n);r>-1&&(i||r!==x)&&(I(r),f.move(n,r,_,s))}},scroll:function t(e,s,n,r){i.Scroll.scroll(e,s,n,function(){var t=P(f.toIndex(g()));I(S?J(t,o):t),r&&r()})},getNext:L,getPrev:k,getAdjacent:B,getEnd:T,setIndex:I,getIndex:function t(e){return e?_:x},toIndex:q,toPage:D,toDest:function t(e){var i=f.toIndex(e);return C?tn(i,0,o):i},hasFocus:O,isBusy:H}},Arrows:function t(e,i,n){var o,r,a=tx(e),l=a.on,d=a.bind,c=a.emit,u=n.classes,h=n.i18n,p=i.Elements,f=i.Controller,g=p.arrows,v=p.track,m=g,y=p.prev,b=p.next,C={};function L(){var t;(t=n.arrows)&&!(y&&b)&&(m=g||T("div",u.arrows),y=w(!0),b=w(!1),o=!0,S(m,[y,b]),g||E(m,v)),y&&b&&(A(C,{prev:y,next:b}),D(m,t?"":"none"),$(m,r=t6+"--"+n.direction),t&&(l([tc,tp,tg,t$,"ei"],B),d(b,"click",s(_,">")),d(y,"click",s(_,"<")),B(),P([y,b],tI,v.id),c("arrows:mounted",y,b))),l(tv,k)}function k(){x(),L()}function x(){a.destroy(),V(m,r),o?(W(g?[y,b]:m),y=b=null):M([y,b],tX)}function _(t){f.go(t,!0)}function w(t){return R('<button class="'+u.arrow+" "+(t?u.prev:u.next)+'" type="button"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false"><path d="'+(n.arrowPath||"m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z")+'" /></svg></button>')}function B(){if(y&&b){var t=e.index,i=f.getPrev(),s=f.getNext(),n=i>-1&&t<i?h.last:h.prev,o=s>-1&&t>s?h.first:h.next;y.disabled=i<0,b.disabled=s<0,P(y,tN,n),P(b,tN,o),c("arrows:updated",y,b,i,s)}}return{arrows:C,mount:L,destroy:x,update:B}},Autoplay:function t(e,i,s){var n,o,r=tx(e),a=r.on,l=r.bind,d=r.emit,c=t_(s.interval,e.go.bind(e,">"),function t(e){var i=h.bar;i&&q(i,"width",100*e+"%"),d("autoplay:playing",e)}),u=c.isPaused,h=i.Elements,p=i.Elements,f=p.root,g=p.toggle,v=s.autoplay,m="pause"===v;function y(){u()&&i.Slides.isEnough()&&(c.start(!s.resetProgress),o=n=m=!1,E(),d(tC))}function $(t){void 0===t&&(t=!0),m=!!t,E(),u()||(c.pause(),d(tL))}function S(){m||(n||o?$(!1):y())}function E(){g&&(b(g,ee,!m),P(g,tN,s.i18n[m?"play":"pause"]))}function C(t){var e=i.Slides.getAt(t);c.set(e&&+O(e.slide,eg)||s.interval)}return{mount:function t(){v&&(s.pauseOnHover&&l(f,"mouseenter mouseleave",function(t){n="mouseenter"===t.type,S()}),s.pauseOnFocus&&l(f,"focusin focusout",function(t){o="focusin"===t.type,S()}),g&&l(g,"click",function(){m?y():$(!0)}),a([th,tb,tg],c.rewind),a(th,C),g&&P(g,tI,h.track.id),m||y(),E())},destroy:c.cancel,play:y,pause:$,isPaused:u}},Cover:function t(e,i,n){var o=tx(e).on;function r(t){i.Slides.forEach(function(e){var i=k(e.container||e.slide,"img");i&&i.src&&a(t,i,e)})}function a(t,e,i){i.style("background",t?'center/cover no-repeat url("'+e.src+'")':"",!0),D(e,t?"none":"")}return{mount:function t(){n.cover&&(o(tk,s(a,!0)),o([tc,tv,tg],s(r,!0)))},destroy:s(r,!1)}},Scroll:function t(e,i,n){var o,r,a=tx(e),l=a.on,d=a.emit,c=e.state.set,u=i.Move,h=u.getPosition,p=u.getLimit,f=u.exceededLimit,g=u.translate,v=e.is(eh),m=1;function y(t,e,n,a,l){var p=h();if(S(),n&&(!v||!f())){var g=i.Layout.sliderSize(),y=to(t)*g*Z(te(t)/g)||0;t=u.toPosition(i.Controller.toDest(t%g))+y}var E,C,L=(E=p,C=t,1>te(E-C));m=1,e=L?0:e||Q(te(t-p)/1.5,800),r=a,o=t_(e,b,s($,p,t,l),1),c(5),d(tb),o.start()}function b(){c(3),r&&r(),d(t$)}function $(t,e,i,s){var o,a,l=h(),d=(t+(e-t)*(o=s,a=n.easingFunc,a?a(o):1-Math.pow(1-o,4))-l)*m;g(l+d),v&&!i&&f()&&(m*=.6,10>te(d)&&y(p(f(!0)),600,!1,r,!0))}function S(){o&&o.cancel()}function E(){o&&!o.isPaused()&&(S(),b())}return{mount:function t(){l(th,S),l([tv,tg],E)},destroy:S,scroll:y,cancel:E}},Drag:function t(e,i,s){var n,r,a,d,c,u,h,p,f=tx(e),g=f.on,v=f.emit,m=f.bind,y=f.unbind,b=e.state,$=i.Move,S=i.Scroll,E=i.Controller,L=i.Elements.track,k=i.Media.reduce,x=i.Direction,_=x.resolve,A=x.orient,w=$.getPosition,B=$.exceededLimit,M=!1;function P(){var t,e=s.drag;h=t=!e,d="free"===e}function T(t){if(u=!1,!h){var e,i,n=F(t);e=t.target,i=s.noDrag,C(e,"."+tJ+", ."+tY)||i&&C(e,i)||!n&&t.button||(E.isBusy()?z(t,!0):(p=n?L:window,c=b.is([4,5]),a=null,m(p,ec,q,ev),m(p,eu,D,ev),$.cancel(),S.cancel(),O(t)))}}function q(t){if(b.is(6)||(b.set(6),v("drag")),t.cancelable){if(c){$.translate(n+(p=H(t),p/(M&&e.is(eh)?5:1)));var i,o,r,a,d,h,p,f=N(t)>200,g=M!==(M=B());(f||g)&&O(t),u=!0,v("dragging"),z(t)}else{i=t,te(H(i))>te(H(i,!0))&&(c=(o=t,r=s.dragMinThreshold,a=l(r),d=a&&r.mouse||0,h=(a?r.touch:+r)||10,te(H(o))>(F(o)?h:d)),z(t))}}}function D(t){var n,o,r,a,l;b.is(6)&&(b.set(3),v("dragged")),c&&(a=(o=r=function t(i){if(e.is(ep)||!M){var s=N(i);if(s&&s<200)return H(i)/s}return 0}(n=t),w()+to(o)*J(te(o)*(s.flickPower||600),d?1/0:i.Layout.listSize()*(s.flickMaxPages||1))),l=s.rewind&&s.rewindByDrag,k(!1),d?E.scroll(a,0,s.snap):e.is(ef)?E.go(0>A(to(r))?l?"<":"-":l?">":"+"):e.is(eh)&&M&&l?E.go(B(!0)?">":"<"):E.go(E.toDest(a),!0),k(!0),z(t)),y(p,ec,q),y(p,eu,D),c=!1}function I(t){!h&&u&&z(t,!0)}function O(t){a=r,r=t,n=w()}function H(t,e){return R(t,e)-R(W(t),e)}function N(t){return G(t)-G(W(t))}function W(t){return r===t&&a||r}function R(t,e){return(F(t)?t.changedTouches[0]:t)["page"+_(e?"Y":"X")]}function F(t){return"undefined"!=typeof TouchEvent&&t instanceof TouchEvent}function X(){return c}function V(t){h=t}return{mount:function t(){m(L,ec,o,ev),m(L,eu,o,ev),m(L,ed,T,ev),m(L,"click",I,{capture:!0}),m(L,"dragstart",z),g([tc,tv],P)},disable:V,isDragging:X}},Keyboard:function t(e,i,s){var o,r,a=tx(e),l=a.on,d=a.bind,c=a.unbind,u=e.root,h=i.Direction.resolve;function p(){var t=s.keyboard;t&&d(o="global"===t?window:u,eb,m)}function f(){c(o,eb)}function g(t){r=t}function v(){var t=r;r=!0,n(function(){r=t})}function m(t){if(!r){var i=ey(t);i===h(tw)?e.go("<"):i===h(tB)&&e.go(">")}}return{mount:function t(){p(),l(tv,f),l(tv,p),l(th,v)},destroy:f,disable:g}},LazyLoad:function t(i,n,o){var r=tx(i),a=r.on,l=r.off,d=r.bind,c=r.emit,u="sequential"===o.lazyLoad,h=[tp,t$],p=[];function f(){e(p),n.Slides.forEach(function(t){X(t.slide,eE).forEach(function(e){var i=O(e,e$),s=O(e,eS);if(i!==e.src||s!==e.srcset){var n=o.classes.spinner,r=e.parentElement,a=k(r,"."+n)||T("span",n,r);p.push([e,t,a]),e.src||D(e,"none")}})}),u?y():(l(h),a(h,g),g())}function g(){(p=p.filter(function(t){var e=o.perPage*((o.preloadPages||1)+1)-1;return!t[1].isWithin(i.index,e)||v(t)})).length||l(h)}function v(t){var e=t[0];$(t[1].slide,eo),d(e,"load error",s(m,t)),P(e,"src",O(e,e$)),P(e,"srcset",O(e,eS)),M(e,e$),M(e,eS)}function m(t,e){var i=t[0],s=t[1];V(s.slide,eo),"error"!==e.type&&(W(t[2]),D(i,""),c(tk,i,s),c(tm)),u&&y()}function y(){p.length&&v(p.shift())}return{mount:function t(){o.lazyLoad&&(f(),a(tg,f))},destroy:s(e,p),check:g}},Pagination:function t(n,o,r){var a,l,d=tx(n),c=d.on,u=d.emit,h=d.bind,p=o.Slides,f=o.Elements,g=o.Controller,v=g.hasFocus,m=g.getIndex,y=g.go,b=o.Direction.resolve,S=f.pagination,E=[];function C(){L(),c([tv,tg,"ei"],C);var t=r.pagination;S&&D(S,t?"":"none"),t&&(c([th,tb,t$],w),function t(){var e=n.length,i=r.classes,o=r.i18n,d=r.perPage,c=v()?g.getEnd()+1:tt(e/d);a=S||T("ul",i.pagination,f.track.parentElement),$(a,l=t5+"--"+_()),P(a,tT,"tablist"),P(a,tN,o.select),P(a,t2,"ttb"===_()?"vertical":"");for(var u=0;u<c;u++){var m=T("li",null,a),y=T("button",{class:i.page,type:"button"},m),b=p.getIn(u).map(function(t){return t.slide.id}),C=!v()&&d>1?o.pageX:o.slideX;h(y,"click",s(k,u)),r.paginationKeyboard&&h(y,"keydown",s(x,u)),P(m,tT,"presentation"),P(y,tT,"tab"),P(y,tI,b.join(" ")),P(y,tN,tr(C,u+1)),P(y,tq,-1),E.push({li:m,button:y,page:u})}}(),w(),u("pagination:mounted",{list:a,items:E},A(n.index)))}function L(){a&&(W(S?i(a.children):a),V(a,l),e(E),a=null),d.destroy()}function k(t){y(">"+t,!0)}function x(t,e){var i=E.length,s=ey(e),n=_(),o=-1;s===b(tB,!1,n)?o=++t%i:s===b(tw,!1,n)?o=(--t+i)%i:"Home"===s?o=0:"End"===s&&(o=i-1);var r=E[o];r&&(I(r.button),y(">"+o),z(e,!0))}function _(){return r.paginationDirection||r.direction}function A(t){return E[g.toPage(t)]}function w(){var t=A(m(!0)),e=A(m());if(t){var i=t.button;V(i,ee),M(i,tH),P(i,tq,-1)}if(e){var s=e.button;$(s,ee),P(s,tH,!0),P(s,tq,"")}u("pagination:updated",{list:a,items:E},t,e)}return{items:E,mount:C,destroy:L,getAt:A,update:w}},Sync:function t(i,n,o){var r=o.isNavigation,a=o.slideFocus,l=[];function d(){var t,e;i.splides.forEach(function(t){t.isParent||(u(i,t.splide),u(t.splide,i))}),r&&(t=tx(i),e=t.on,e(tf,f),e("sk",g),e([tc,tv],p),l.push(t),t.emit(tE,i.splides))}function c(){l.forEach(function(t){t.destroy()}),e(l)}function u(t,e){var i=tx(t);i.on(th,function(t,i,s){e.go(e.is(ep)?s:t)}),l.push(i)}function p(){P(n.Elements.list,t2,"ttb"===o.direction?"vertical":"")}function f(t){i.go(t.index)}function g(t,e){m(eC,ey(e))&&(f(t),z(e))}return{setup:s(n.Media.set,{slideFocus:h(a)?r:a},!0),mount:d,destroy:c,remount:function t(){c(),d()}}},Wheel:function t(e,i,s){var n=tx(e).bind,o=0;function r(t){if(t.cancelable){var n,r=t.deltaY,a=r<0,l=G(t),d=s.wheelMinThreshold||0,c=s.wheelSleep||0;te(r)>d&&l-o>c&&(e.go(a?"<":">"),o=l),n=a,(!s.releaseWheel||e.state.is(4)||-1!==i.Controller.getAdjacent(n))&&z(t)}}return{mount:function t(){s.wheel&&n(i.Elements.track,"wheel",r,ev)}}},Live:function t(e,i,n){var o=tx(e).on,r=i.Elements.track,a=n.live&&!n.isNavigation,l=T("span",et),d=t_(90,s(c,!1));function c(t){P(r,t3,t),t?(S(r,l),d.start()):(W(l),d.cancel())}function u(t){a&&P(r,tF,t?"off":"polite")}return{mount:function t(){a&&(u(!i.Autoplay.isPaused()),P(r,t9,!0),l.textContent="…",o(tC,s(u,!0)),o(tL,s(u,!1)),o([tp,t$],s(c,!0)))},disable:u,destroy:function t(){M(r,[tF,t9,t3]),W(l)}}}}),ek={type:"slide",role:"region",speed:400,perPage:1,cloneStatus:!0,arrows:!0,pagination:!0,paginationKeyboard:!0,interval:5e3,pauseOnHover:!0,pauseOnFocus:!0,resetProgress:!0,easing:"cubic-bezier(0.25, 1, 0.5, 1)",drag:!0,direction:"ltr",trimSpace:!0,focusableNodes:"a, button, textarea, input, select, iframe",live:!0,classes:{slide:tG,clone:tU,arrows:t6,arrow:tY,prev:t7,next:tK,pagination:t5,page:tJ,spinner:t0+"spinner"},i18n:{prev:"Previous slide",next:"Next slide",first:"Go to first slide",last:"Go to last slide",slideX:"Go to slide %s",pageX:"Go to page %s",play:"Start autoplay",pause:"Pause autoplay",carousel:"carousel",slide:"slide",select:"Select a slide to show",slideLabel:"%s of %s"},reducedMotion:{speed:0,rewindSpeed:0,autoplay:"pause"}};function ex(t,e,i){var s=e.Slides;function r(){s.forEach(function(t){t.style("transform","translateX(-"+100*t.index+"%)")})}return{mount:function e(){tx(t).on([tc,tg],r)},start:function t(e,o){s.style("transition","opacity "+i.speed+"ms "+i.easing),n(o)},cancel:o}}function e_(t,e,i){var n,o=e.Move,r=e.Controller,a=e.Scroll,l=e.Elements.list,d=s(q,l,"transition");function c(){d(""),a.cancel()}return{mount:function e(){tx(t).bind(l,"transitionend",function(t){t.target===l&&n&&(c(),n())})},start:function e(s,l){var c=o.toPosition(s,!0),u=o.getPosition(),h=function e(s){var n=i.rewindSpeed;if(t.is(eh)&&n){var o=r.getIndex(!0),a=r.getEnd();if(0===o&&s>=a||o>=a&&0===s)return n}return i.speed}(s);te(c-u)>=1&&h>=1?i.useScroll?a.scroll(c,h,!1,l):(d("transform "+h+"ms "+i.easing),o.translate(c,!0),n=l):(o.jump(s),l())},cancel:c}}var eA=function(){function t(e,i){this.event=tx(),this.Components={},this.state=function t(e){var i=1;function s(t){i=t}return{set:s,is:function t(e){return m(g(e),i)}}}(1),this.splides=[],this._o={},this._E={};var s=u(e)?F(document,e):e;K(s,s+" is invalid."),this.root=s,i=w({label:O(s,tN)||"",labelledby:O(s,tW)||""},ek,t.defaults,i||{});try{w(i,JSON.parse(O(s,Y)))}catch(n){K(!1,"Invalid JSON")}this._o=Object.create(w({},i))}var s=t.prototype;return s.mount=function t(e,i){var s=this,n=this.state,o=this.Components;K(n.is([1,7]),"Already mounted!"),n.set(1),this._C=o,this._T=i||this._T||(this.is(ef)?ex:e_),this._E=e||this._E;var r=A({},eL,this._E,{Transition:this._T});return _(r,function(t,e){var i=t(s,o,s._o);o[e]=i,i.setup&&i.setup()}),_(o,function(t){t.mount&&t.mount()}),this.emit(tc),$(this.root,"is-initialized"),n.set(3),this.emit(tu),this},s.sync=function t(e){return this.splides.push({splide:e}),e.splides.push({splide:this,isParent:!0}),this.state.is(3)&&(this._C.Sync.remount(),e.Components.Sync.remount()),this},s.go=function t(e){return this._C.Controller.go(e),this},s.on=function t(e,i){return this.event.on(e,i),this},s.off=function t(e){return this.event.off(e),this},s.emit=function t(e){var s;return(s=this.event).emit.apply(s,[e].concat(i(arguments,1))),this},s.add=function t(e,i){return this._C.Slides.add(e,i),this},s.remove=function t(e){return this._C.Slides.remove(e),this},s.is=function t(e){return this._o.type===e},s.refresh=function t(){return this.emit(tg),this},s.destroy=function t(i){void 0===i&&(i=!0);var s=this.event,n=this.state;return n.is(1)?tx(this).on(tu,this.destroy.bind(this,i)):(_(this._C,function(t){t.destroy&&t.destroy(i)},!0),s.emit(tS),s.destroy(),i&&e(this.splides),n.set(7)),this},_createClass(t,[{key:"options",get:function t(){return this._o},set:function t(e){this._C.Media.set(e,!0,!0)}},{key:"length",get:function t(){return this._C.Slides.getLength(!0)}},{key:"index",get:function t(){return this._C.Controller.getIndex()}}]),t}();return eA.defaults={},eA.STATES={CREATED:1,MOUNTED:2,IDLE:3,MOVING:4,SCROLLING:5,DRAGGING:6,DESTROYED:7},eA});

/* ========== Splide component ========== */
class SplideComponent extends HTMLElement {
  connectedCallback() {
    this.initSlider();
  }

  disconnectedCallback() {
    this.destroySlider();
  }

  destroySlider() {
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.loadHandler) {
      window.removeEventListener("load", this.loadHandler);
      this.loadHandler = null;
    }
    if (this.splide) {
      try { this.splide.destroy(true); } catch (e) {}
      this.splide = null;
    }
  }

  initSlider() {
    const SplideLib = window.ThemeSplide || window.Splide;
    this.sliderContainer = this.querySelector(".splide");
    if (!this.sliderContainer || typeof SplideLib !== "function") return;

    this.destroySlider();

    const d = this.dataset;
    const toInt = (value, fallback) => {
      const n = parseInt(value, 10);
      return isNaN(n) ? fallback : n;
    };

    this.type = d.type || "slide";
    this.typeMobile = d.typeMobile || this.type;
    this.direction = d.direction || "ltr";
    this.rewind = this.type === "fade";
    this.autoplay = d.autoplay === "true";
    this.autoplaySpeed = toInt(d.autoplaySpeed, 5) * 1000;
    this.drag = d.drag !== "false" && (d.drag !== "free" || "free");
    this.focus = d.focus || 0;
    this.trimSpace = d.trimSpace !== "false";
    this.arrows = d.arrows !== "false";
    this.arrowsColor = d.arrowsColor ? ` color-${d.arrowsColor}` : "";
    this.pagination = d.pagination !== "false";
    this.omitEnd = d.omitEnd !== "false";
    this.dotsColor = d.dotsColor ? ` color-${d.dotsColor} dots-custom-color` : "";
    this.slidesDesktop = toInt(d.slidesDesktop, 3);
    this.autoWidth = d.autoWidth === "true";
    this.slidesMobile = toInt(d.slidesMobile, 1);
    this.perMoveDesktop = toInt(d.perMoveDesktop, 1);
    this.perMoveMobile = toInt(d.perMoveMobile, 1);
    this.gapDesktop = toInt(d.gapDesktop, 30);
    this.gapMobile = toInt(d.gapMobile, 15);
    this.paddingCalcDesktop = d.paddingCalcDesktop === "true";
    this.rightPaddingDesktop = toInt(d.sidePaddingDesktop, 0);
    this.leftPaddingDesktop = this.paddingCalcDesktop ? 0 : this.rightPaddingDesktop;
    this.paddingCalcMobile = d.paddingCalcMobile === "true";
    this.rightPaddingMobile = toInt(d.sidePaddingMobile, 0);
    this.leftPaddingMobile = this.paddingCalcMobile ? 0 : this.rightPaddingMobile;
    this.destroyDesktop = d.destroyDesktop === "true";
    this.destroyMobile = d.destroyMobile === "true";

    this.config = {
      type: this.type,
      direction: this.direction,
      rewind: this.rewind,
      autoplay: this.autoplay,
      interval: this.autoplaySpeed,
      drag: this.drag,
      focus: this.focus,
      trimSpace: this.trimSpace,
      arrows: this.arrows,
      pagination: this.pagination,
      omitEnd: this.omitEnd,
      perPage: this.slidesDesktop,
      perMove: this.perMoveDesktop,
      autoWidth: this.autoWidth,
      gap: this.gapDesktop,
      paddingCalc: this.paddingCalcDesktop,
      padding: { left: this.leftPaddingDesktop, right: this.rightPaddingDesktop },
      destroy: this.destroyDesktop,
      classes: {
        arrow: `splide__arrow${this.arrowsColor}`,
        page: `splide__pagination__page${this.dotsColor}`
      },
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      breakpoints: {
        749: {
          omitEnd: this.omitEnd,
          type: this.typeMobile,
          perPage: this.slidesMobile,
          perMove: this.perMoveMobile,
          gap: this.gapMobile,
          paddingCalc: this.paddingCalcMobile,
          padding: { left: this.leftPaddingMobile, right: this.rightPaddingMobile },
          destroy: this.destroyMobile
        }
      }
    };

    const splide = new SplideLib(this.sliderContainer, this.config);
    this.splide = splide;

    const getActiveSlide = () => (this.splide ? this.splide.Components.Elements.slides[this.splide.index] : null);

    splide.on("mounted", () => {
      const activeSlide = getActiveSlide();
      if (activeSlide) {
        const img = activeSlide.querySelector("img");
        const activate = () => {
          activeSlide.classList.add("is-instant-active");
          this.setActiveSlideHeight(activeSlide);
        };
        if (img && !img.complete) {
          img.addEventListener("load", activate, { once: true });
          img.addEventListener("error", activate, { once: true });
        } else {
          activate();
        }
      }

      this.resizeHandler = debounce(() => this.setActiveSlideHeight(getActiveSlide()), 200);
      window.addEventListener("resize", this.resizeHandler);
    });

    splide.on("move", () => {
      const slides = splide.Components.Elements.slides;
      if (!slides) return;
      const activeSlide = slides[splide.index];
      if (!activeSlide) return;
      slides.forEach((slide) => slide.classList.remove("is-instant-active"));
      activeSlide.classList.add("is-instant-active");
      this.setActiveSlideHeight(activeSlide);
    });

    if (d.imageSlider === "true") {
      const syncHeight = () => this.setActiveSlideHeight(getActiveSlide());
      if (document.readyState === "complete") {
        requestAnimationFrame(syncHeight);
      } else {
        this.loadHandler = syncHeight;
        window.addEventListener("load", this.loadHandler, { once: true });
      }
    }

    splide.mount();
  }

  setActiveSlideHeight(slide) {
    if (!slide) return;
    this.style.setProperty("--active-slide-height", `${slide.offsetHeight}px`);
  }
}
defineElement("splide-component", SplideComponent);

/* ========== Details disclosure / header menu ========== */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    if (!this.mainDetailsToggle) return;
    const summary = this.mainDetailsToggle.querySelector("summary");
    this.content = summary ? summary.nextElementSibling : null;

    this.mainDetailsToggle.addEventListener("focusout", this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.content) return;
    if (!this.animations) this.animations = typeof this.content.getAnimations === "function" ? this.content.getAnimations() : [];

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    if (!this.mainDetailsToggle) return;
    this.mainDetailsToggle.removeAttribute("open");
    const summary = this.mainDetailsToggle.querySelector("summary");
    if (summary) summary.setAttribute("aria-expanded", false);
  }
}
defineElement("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue("--header-bottom-position-desktop") !== "") return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}
defineElement("header-menu", HeaderMenu);

/* ========== Details modal ========== */
class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector("details");
    this.summaryToggle = this.querySelector("summary");
    if (!this.detailsContainer || !this.summaryToggle) return;

    this.detailsContainer.addEventListener("keyup", (event) => {
      if (event.code && event.code.toUpperCase() === "ESCAPE") this.close();
    });
    this.summaryToggle.addEventListener("click", this.onSummaryClick.bind(this));

    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener("click", this.close.bind(this));

    this.summaryToggle.setAttribute("role", "button");
  }

  isOpen() {
    return this.detailsContainer.hasAttribute("open");
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest("details").hasAttribute("open") ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains("modal-overlay")) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest("details").setAttribute("open", true);
    document.body.addEventListener("click", this.onBodyClickEvent);
    document.body.classList.add("overflow-hidden");

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute("open");
    if (this.onBodyClickEvent) document.body.removeEventListener("click", this.onBodyClickEvent);
    document.body.classList.remove("overflow-hidden");
  }
}
defineElement("details-modal", DetailsModal);

/* ========== Hotspots ========== */
let hotspotButtons = [];

function registerHotspotButton(button) {
  if (!hotspotButtons.includes(button)) hotspotButtons.push(button);
}

function unregisterHotspotButton(button) {
  const index = hotspotButtons.indexOf(button);
  if (index !== -1) hotspotButtons.splice(index, 1);
}

document.addEventListener("mousedown", (event) => {
  for (const button of hotspotButtons) {
    if (button.dataset.open === "true" && !button.contains(event.target)) button.closeModal();
  }
});

class HotspotButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector(".hotspot-btn");
    this.content = this.querySelector(".hotspot__content");
    this.type = this.dataset.type;
    this.openEvent = this.dataset.openEvent;
    this.header = document.querySelector(".section-header");
    this.stickyHeader = document.querySelector("sticky-header");
    this.mobileOverlay = this.querySelector(".hotspot-overlay");

    if (!this.button || !this.content) return;

    this.button.addEventListener("click", this.toggleModal.bind(this));

    if (this.openEvent === "hover") {
      this.button.addEventListener("mouseover", this.openModal.bind(this));
      this.button.addEventListener("mouseout", this.startCheckingMouseLeave.bind(this));
      this.content.addEventListener("mouseover", this.stopCheckingMouseLeave.bind(this));
      this.content.addEventListener("mouseout", this.startCheckingMouseLeave.bind(this));
    }

    if (this.mobileOverlay) this.mobileOverlay.addEventListener("click", this.closeModal.bind(this));
  }

  connectedCallback() {
    registerHotspotButton(this);
  }

  disconnectedCallback() {
    unregisterHotspotButton(this);
  }

  startCheckingMouseLeave() {
    this.checkingMouseLeave = true;
    setTimeout(() => {
      if (this.checkingMouseLeave) this.closeModal();
    }, 200);
  }

  stopCheckingMouseLeave() {
    this.checkingMouseLeave = false;
  }

  toggleModal() {
    this.dataset.open === "true" ? this.closeModal() : this.openModal();
  }

  openModal() {
    if (!this.content) return;
    clearTimeout(this.directionTimer);

    let headerHeight = this.header ? this.header.clientHeight : 0;
    if (!this.header || this.header.classList.contains("shopify-section-header-hidden") || !this.stickyHeader) headerHeight = 0;

    if (this.content.getBoundingClientRect().top - headerHeight < 0) this.dataset.direction = "bottom";
    this.dataset.open = "true";
  }

  closeModal() {
    this.dataset.open = "false";
    clearTimeout(this.directionTimer);
    this.directionTimer = setTimeout(() => {
      this.dataset.direction = "";
    }, 100);
  }
}
defineElement("hotspot-button", HotspotButton);

/* ========== Parallax hero ========== */
class ParallaxHero extends HTMLElement {
  constructor() {
    super();
    this.overlays = this.querySelectorAll(".parallax-hero__layer");
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.handleScroll();
      });
    };
  }

  connectedCallback() {
    this.handleScroll();
    window.addEventListener("scroll", this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
  }

  handleScroll() {
    let { top, height } = this.getBoundingClientRect();
    if (top > window.innerHeight) return;

    top = top >= 0 ? 0 : -top;
    if (!height || top > height) return;

    const progress = top / height;
    const num = (value, fallback) => {
      const n = parseFloat(value);
      return isNaN(n) ? fallback : n;
    };

    this.overlays.forEach((layer) => {
      const translateY = top * (num(layer.dataset.scrollY, 0) / 100);
      const translateX = num(layer.dataset.scrollX, 0) * progress;
      const zoom = 100 + (num(layer.dataset.zoom, 100) - 100) * progress;
      const rotation = 3.6 * num(layer.dataset.rotation, 0) * progress;
      layer.style.transform = `translateY(${translateY}px) translateX(${translateX}%) scale(${zoom / 100}) rotate(${rotation}deg)`;
    });
  }
}
defineElement("parallax-hero", ParallaxHero);

/* ========== Content tabs ========== */
class ContentTabs extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll(".content-tab-button");
    this.tabs = this.querySelectorAll(".content-tab");
    this.tabsContainer = this.querySelector(".content-tabs__tabs");
    this.activeButton = this.querySelector(".content-tab-button--active") || this.buttons[0] || null;
    this.activeTab = this.querySelector(".content-tab--active") || this.tabs[0] || null;
    this.isMovingAnimation = this.dataset.animation === "moving";
    this.activeBg = this.isMovingAnimation
      ? this.querySelector(".content-tab-buttom__active-bg, .content-tab-button__active-bg")
      : null;
    if (!this.activeBg) this.isMovingAnimation = false;

    this.onResize = () => {
      this.setHeight();
      if (this.isMovingAnimation) this.handleActiveBg();
    };

    this.buttons.forEach((button) => button.addEventListener("click", this.handleClick.bind(this)));
  }

  connectedCallback() {
    this.setHeight();

    if (this.isMovingAnimation && !this.bgReady) {
      this.handleActiveBg();
      void this.activeBg.offsetWidth;
      this.activeBg.style.transitionDuration = "0.4s";
      this.bgReady = true;
    }

    window.addEventListener("resize", this.onResize);

    if ("ResizeObserver" in window && this.tabs.length) {
      this.resizeObserver = this.resizeObserver || new ResizeObserver(() => this.setHeight());
      this.tabs.forEach((tab) => this.resizeObserver.observe(tab));
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  handleClick(event) {
    this.activeButton = event.currentTarget;
    this.buttons.forEach((button) => button.classList.remove("content-tab-button--active"));
    this.activeButton.classList.add("content-tab-button--active");

    this.tabs.forEach((tab) => {
      tab.classList.remove("content-tab--active");
      if (tab.dataset.index === this.activeButton.dataset.index) this.activeTab = tab;
    });
    if (this.activeTab) this.activeTab.classList.add("content-tab--active");

    this.setHeight();
    if (this.isMovingAnimation) this.handleActiveBg();
  }

  setHeight() {
    if (this.tabsContainer && this.activeTab) {
      this.tabsContainer.style.height = this.activeTab.clientHeight + "px";
    }
  }

  handleActiveBg() {
    if (!this.activeBg || !this.activeButton) return;
    const rect = this.activeButton.getBoundingClientRect();
    this.activeBg.style.width = rect.width + "px";
    this.activeBg.style.height = rect.height + "px";
    this.activeBg.style.top = this.activeButton.offsetTop + "px";
    this.activeBg.style.left = this.activeButton.offsetLeft + "px";
  }
}
defineElement("content-tabs", ContentTabs);

/* ========== Insta stories ========== */
class InstaStories extends HTMLElement {
  constructor() {
    super();
    this.openButtons = this.querySelectorAll(".insta-story-open-btn");
    this.openButtonsOverflowContainer = this.querySelector(".insta-stories__open-buttons-container");
    this.openButtonsContainer = this.querySelector(".insta-stories__open-buttons");
    this.openBtnsPrev = this.querySelector(".insta-stories__open-btns-prev");
    this.openBtnsNext = this.querySelector(".insta-stories__open-btns-next");
    this.closeButtons = this.querySelectorAll(".insta-stories__close-button");
    this.modal = this.querySelector(".insta-stories__modal");
    this.modalOpen = false;
    this.slider = this.querySelector(".insta-stories__slider");
    this.stories = this.querySelectorAll(".insta-story");
    this.prevBtns = this.querySelectorAll(".insta-story__prev");
    this.nextBtns = this.querySelectorAll(".insta-story__next");
    this.slideBtns = this.querySelectorAll(".insta-story__slide-btn");
    this.activeIndex = 0;
    this.activeStory = this.stories[0];

    const lastIndex = parseInt(this.dataset.lastIndex, 10);
    this.lastIndex = Math.min(isNaN(lastIndex) ? this.stories.length - 1 : lastIndex, this.stories.length - 1);

    this.pauseResumeBtns = this.querySelectorAll(".insta-story__pause-resume-btn");
    this.isPaused = false;
    this.volumeBtns = this.querySelectorAll(".insta-story__volume-btn");
    this.isMuted = true;
    this.remainingTime = 0;
    this.autoplayStartTime = null;

    if (!this.modal || !this.slider || !this.stories.length) return;

    this.initStories();
    this.initButtonsSlider();

    this.openButtons.forEach((btn) => btn.addEventListener("click", this.openModal.bind(this)));
    this.closeButtons.forEach((btn) => btn.addEventListener("click", this.closeModal.bind(this)));
    this.prevBtns.forEach((btn) => btn.addEventListener("click", this.storyPrevBtnClick.bind(this)));
    this.nextBtns.forEach((btn) => btn.addEventListener("click", this.storyNextBtnClick.bind(this)));
    this.slideBtns.forEach((btn) => btn.addEventListener("click", this.slideBtnClick.bind(this)));
    this.pauseResumeBtns.forEach((btn) => btn.addEventListener("click", this.togglePauseResume.bind(this)));
    this.volumeBtns.forEach((btn) => btn.addEventListener("click", this.toggleIsMuted.bind(this)));

    this.slider.addEventListener("touchstart", this.touchStartHandler.bind(this), { passive: true });
    this.slider.addEventListener("touchend", this.touchEndHandler.bind(this), { passive: true });

    this.onKeydown = (event) => {
      if (!this.modalOpen) return;
      switch (event.key) {
        case "Escape": this.closeModal(); break;
        case "ArrowLeft": this.storyPrevBtnClick(); break;
        case "ArrowRight": this.storyNextBtnClick(); break;
      }
    };
  }

  connectedCallback() {
    if (this.onKeydown) document.addEventListener("keydown", this.onKeydown);
  }

  disconnectedCallback() {
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown);
    clearTimeout(this.autoplayTimeout);
  }

  getMediaDuration(media) {
    const seconds = parseFloat(media && media.getAttribute("data-duration"));
    return (isNaN(seconds) || seconds <= 0 ? 5 : seconds) * 1000;
  }

  getActiveMedia() {
    const story = this.activeStory;
    if (!story) return null;
    return story.querySelectorAll(".insta-story__media")[parseInt(story.dataset.activeMediaIndex, 10) || 0] || null;
  }

  playVideo(video) {
    const promise = video.play();
    if (promise && typeof promise.catch === "function") promise.catch(() => {});
  }

  startTimer(duration) {
    clearTimeout(this.autoplayTimeout);
    this.remainingTime = duration;
    this.autoplayStartTime = Date.now();
    this.autoplayTimeout = setTimeout(() => this.storyNextBtnClick(), duration);
  }

  autoplay() {
    const story = this.activeStory;
    if (!story) return;
    const index = parseInt(story.dataset.activeMediaIndex, 10) || 0;
    const media = story.querySelectorAll(".insta-story__media")[index];
    if (!media) return;

    this.updateProgressBars(index);
    this.startTimer(this.getMediaDuration(media));
  }

  storyPrevBtnClick() {
    const story = this.activeStory;
    if (!story) return;
    const index = parseInt(story.dataset.activeMediaIndex, 10) || 0;
    index === 0 ? this.changeActiveStory(this.activeIndex - 1) : this.changeActiveMedia(index - 1);
  }

  storyNextBtnClick() {
    const story = this.activeStory;
    if (!story) return;
    story.dataset.activeMediaIndex === story.dataset.lastMediaIndex
      ? this.changeActiveStory(this.activeIndex + 1)
      : this.changeActiveMedia((parseInt(story.dataset.activeMediaIndex, 10) || 0) + 1);
  }

  slideBtnClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!isNaN(index)) this.changeActiveStory(index);
  }

  changeActiveStory(index) {
    clearTimeout(this.autoplayTimeout);

    if (index > this.lastIndex) {
      this.closeModal();
      return;
    }
    if (index < 0) {
      this.changeActiveMedia(0);
      return;
    }

    const prevStory = this.stories[this.activeIndex];
    if (prevStory) {
      const prevMediaIndex = parseInt(prevStory.dataset.activeMediaIndex, 10) || 0;

      if (prevStory.dataset.played === "true" || prevMediaIndex > 0) {
        const item = prevStory.querySelectorAll(".insta-story__progress-item")[prevMediaIndex];
        if (item) {
          item.classList.remove("insta-story__progress-item--active");
          if (index > this.activeIndex) item.classList.add("insta-story__progress-item--completed");
        }
      }

      const prevMedia = prevStory.querySelectorAll(".insta-story__media")[prevMediaIndex];
      if (prevMedia && prevMedia.getAttribute("data-type") === "video") {
        const video = prevMedia.querySelector("video");
        if (video) {
          video.pause();
          if (!this.isPaused) video.currentTime = 0;
        }
      }
    }

    this.activeIndex = index;
    this.activeStory = this.stories[index];
    if (!this.activeStory) return;

    this.stories.forEach((story) => story.classList.remove("insta-story--active"));
    this.activeStory.classList.add("insta-story--active");
    this.activeStory.dataset.played = "true";
    this.slider.style.transform = `translateX(calc(var(--story-width) * ${-1 * index}))`;

    this.changeActiveMedia(parseInt(this.activeStory.dataset.activeMediaIndex, 10) || 0);
  }

  changeActiveMedia(index) {
    const story = this.activeStory;
    if (!story || index < 0) return;

    const lastMediaIndex = parseInt(story.dataset.lastMediaIndex, 10);
    if (index > lastMediaIndex) return;

    clearTimeout(this.autoplayTimeout);
    story.dataset.activeMediaIndex = index;

    const mediaItems = story.querySelectorAll(".insta-story__media");
    const timePosted = story.querySelector(".insta-story__time-posted");

    mediaItems.forEach((media, i) => {
      const video = media.getAttribute("data-type") === "video" ? media.querySelector("video") : null;

      if (i === index) {
        media.style.display = "block";
        if (video) {
          video.muted = this.isMuted;
          if (!this.isPaused) this.playVideo(video);
        }
        if (timePosted) timePosted.innerHTML = media.dataset.timePosted || "";
      } else {
        media.style.display = "";
        if (video) {
          video.pause();
          if (!this.isPaused) video.currentTime = 0;
        }
      }
    });

    this.updateProgressBars(index);

    if (this.isPaused) {
      this.remainingTime = this.getMediaDuration(mediaItems[index]);
      this.autoplayStartTime = null;
    } else {
      this.autoplay();
    }

    story.querySelectorAll(".insta-story__prev").forEach((btn) => {
      this.activeIndex === 0 && index === 0 ? btn.setAttribute("disabled", "") : btn.removeAttribute("disabled");
    });
    story.querySelectorAll(".insta-story__next").forEach((btn) => {
      this.activeIndex === this.lastIndex && index === lastMediaIndex ? btn.setAttribute("disabled", "") : btn.removeAttribute("disabled");
    });
  }

  updateProgressBars(activeIndex) {
    const story = this.activeStory;
    if (!story) return;
    story.querySelectorAll(".insta-story__progress-item").forEach((item, i) => {
      item.classList.remove("insta-story__progress-item--completed", "insta-story__progress-item--active");
      if (i < activeIndex) item.classList.add("insta-story__progress-item--completed");
      else if (i === activeIndex) item.classList.add("insta-story__progress-item--active");
    });
  }

  openModal(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10) || 0;
    window.scrollBy(0, -1);
    this.modal.dataset.open = "true";
    this.modalOpen = true;
    document.body.classList.add("overflow-hidden");
    this.changeActiveStory(index);
  }

  closeModal() {
    clearTimeout(this.autoplayTimeout);
    this.querySelectorAll("video").forEach((video) => video.pause());
    this.modal.dataset.open = "false";
    this.modalOpen = false;
    document.body.classList.remove("overflow-hidden");

    if (this.isPaused) {
      this.isPaused = false;
      this.pauseResumeBtns.forEach((btn) => btn.setAttribute("data-paused", "false"));
      this.querySelectorAll(".insta-story__progress-bar").forEach((bar) => (bar.style.animationPlayState = "running"));
    }
  }

  initButtonsSlider() {
    const overflow = this.openButtonsOverflowContainer;
    const inner = this.openButtonsContainer;
    if (!overflow || !inner || !this.openButtons.length) return;

    const isTouch = () => "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const fits = () => overflow.clientWidth > inner.clientWidth;

    let startX = 0;
    let currentX = 0;
    let lastX = 0;

    const setPosition = (x) => {
      inner.style.transform = `translateX(${x}px)`;
      currentX = x;
    };

    overflow.addEventListener("touchstart", (event) => {
      if (fits()) return;
      this.isDragging = true;
      startX = event.touches[0].clientX - lastX;
    }, { passive: true });

    overflow.addEventListener("touchmove", (event) => {
      if (!this.isDragging) return;
      const maxOffset = Math.max(0, inner.offsetWidth - overflow.offsetWidth);
      const delta = event.touches[0].clientX - startX;
      setPosition(Math.max(-maxOffset, Math.min(0, delta)));
    }, { passive: true });

    overflow.addEventListener("touchend", () => {
      this.isDragging = false;
      lastX = currentX;
    }, { passive: true });

    const getStep = () => {
      const btnWidth = parseFloat(getComputedStyle(this.openButtons[0]).width) || this.openButtons[0].offsetWidth;
      const gap = parseFloat(getComputedStyle(inner).columnGap) || 0;
      const perView = Math.floor(overflow.clientWidth / (btnWidth + gap));
      return Math.max(1, perView - 1) * (btnWidth + gap);
    };

    const updateNav = () => {
      if (!this.openBtnsPrev || !this.openBtnsNext) return;
      if (isTouch() || fits()) {
        this.openBtnsPrev.style.display = "none";
        this.openBtnsNext.style.display = "none";
        return;
      }
      this.openBtnsPrev.style.display = overflow.scrollLeft <= 0 ? "none" : "grid";
      this.openBtnsNext.style.display = overflow.scrollLeft >= overflow.scrollWidth - overflow.clientWidth - 1 ? "none" : "grid";
    };

    if (this.openBtnsPrev) {
      this.openBtnsPrev.addEventListener("click", () => overflow.scrollBy({ left: -getStep(), behavior: "smooth" }));
    }
    if (this.openBtnsNext) {
      this.openBtnsNext.addEventListener("click", () => overflow.scrollBy({ left: getStep(), behavior: "smooth" }));
    }

    overflow.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", debounce(updateNav, 250));
    updateNav();
  }

  initStories() {
    this.stories.forEach((story) => {
      const mediaItems = story.querySelectorAll(".insta-story__media");
      story.dataset.lastMediaIndex = mediaItems.length - 1;
      story.dataset.played = "false";
      if (!story.dataset.activeMediaIndex) story.dataset.activeMediaIndex = 0;

      const progress = story.querySelector(".insta-story__progress");
      if (progress) {
        progress.querySelectorAll(".insta-story__progress-item").forEach((el) => el.remove());

        mediaItems.forEach((media) => {
          const item = document.createElement("span");
          item.className = "insta-story__progress-item";
          item.style.setProperty("--duration", `${this.getMediaDuration(media) / 1000}s`);

          const bar = document.createElement("span");
          bar.className = "insta-story__progress-bar";
          item.appendChild(bar);
          progress.appendChild(item);
        });
      }

      const activeMedia = mediaItems[parseInt(story.dataset.activeMediaIndex, 10) || 0];
      if (activeMedia) activeMedia.style.display = "block";
    });
  }

  togglePauseResume(event) {
    const isPaused = event.currentTarget.getAttribute("data-paused") === "true";
    isPaused ? this.resumeStory() : this.pauseStory();
    this.pauseResumeBtns.forEach((btn) => btn.setAttribute("data-paused", isPaused ? "false" : "true"));
  }

  pauseStory() {
    this.isPaused = true;
    clearTimeout(this.autoplayTimeout);

    if (this.autoplayStartTime) {
      this.remainingTime = Math.max(0, this.remainingTime - (Date.now() - this.autoplayStartTime));
    }
    this.autoplayStartTime = null;

    const media = this.getActiveMedia();
    if (media && media.getAttribute("data-type") === "video") {
      const video = media.querySelector("video");
      if (video) video.pause();
    }

    this.querySelectorAll(".insta-story__progress-bar").forEach((bar) => (bar.style.animationPlayState = "paused"));
  }

  resumeStory() {
    this.isPaused = false;
    this.querySelectorAll(".insta-story__progress-bar").forEach((bar) => (bar.style.animationPlayState = "running"));

    const media = this.getActiveMedia();
    if (media && media.getAttribute("data-type") === "video") {
      const video = media.querySelector("video");
      if (video) this.playVideo(video);
    }

    this.startTimer(this.remainingTime > 0 ? this.remainingTime : this.getMediaDuration(media));
  }

  toggleIsMuted() {
    this.isMuted = !this.isMuted;
    this.querySelectorAll("video").forEach((video) => (video.muted = this.isMuted));
    this.volumeBtns.forEach((btn) => btn.setAttribute("data-muted", this.isMuted ? "true" : "false"));
  }

  touchStartHandler(event) {
    this.touchStartX = event.touches[0].clientX;
  }

  touchEndHandler(event) {
    if (this.touchStartX === undefined) return;
    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    this.touchStartX = undefined;
    if (Math.abs(deltaX) < 50) return;

    if (deltaX < 0) {
      if (this.activeIndex < this.lastIndex) this.changeActiveStory(this.activeIndex + 1);
    } else if (this.activeIndex > 0) {
      this.changeActiveStory(this.activeIndex - 1);
    }
  }
}
defineElement("insta-stories", InstaStories);

/* ========== Terms and conditions checkbox (cart drawer) ========== */
class TncCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checked = false;
    this.onCheckoutClick = this.handleButtonClick.bind(this);
    this.addEventListener("click", this.handleCheckboxClick.bind(this));
    this.addEventListener("keydown", (event) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        this.handleCheckboxClick();
      }
    });
  }

  connectedCallback() {
    this.checkoutButton = document.querySelector("#CartDrawer-Checkout");
    this.disableButton = this.dataset.disableButton === "true";
    this.warningText = this.dataset.warningText || "";
    this.warningTextElement = document.querySelector(`.tnc-checkbox-warning--${this.dataset.warningPosition}`);
    this.checked = this.dataset.checked === "true";

    if (!this.hasAttribute("role")) this.setAttribute("role", "checkbox");
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");
    this.setAttribute("aria-checked", this.checked);

    if (!this.checkoutButton || !this.warningTextElement) return;

    this.warningTextElement.innerHTML = this.warningText;
    this.updateButtonState();
    this.checkoutButton.addEventListener("click", this.onCheckoutClick);
  }

  disconnectedCallback() {
    if (this.checkoutButton) this.checkoutButton.removeEventListener("click", this.onCheckoutClick);
  }

  updateButtonState() {
    if (this.disableButton && this.checkoutButton) {
      this.checkoutButton.classList.toggle("disabled", !this.checked);
    }
  }

  handleCheckboxClick() {
    this.checked = !this.checked;
    this.dataset.checked = this.checked;
    this.setAttribute("aria-checked", this.checked);
    if (this.checked && this.warningTextElement) this.warningTextElement.classList.add("hidden");
    this.updateButtonState();
  }

  handleButtonClick(event) {
    if (this.checked) return;
    event.preventDefault();
    if (this.warningTextElement) this.warningTextElement.classList.remove("hidden");
  }
}
defineElement("tnc-chekcbox", TncCheckbox);
defineElement("tnc-checkbox", class extends TncCheckbox {});